import React, { Fragment, useEffect, useRef, useState } from "react";
import { styled } from '@mui/material/styles';
import Autocomplete from "@mui/material/Autocomplete";
import EventIcon from "@mui/icons-material/Event";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import { IconButton } from "@mui/material";
import RemoveIcon from "@mui/icons-material/Remove";
import TextField from "@mui/material/TextField";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import HtmlTooltip from "./HtmlTooltip";
import InfoIcon from "@mui/icons-material/Info";
import {
  getStates,
  getCounties,
  getCities,
  setStateSelected,
  setCountySelected,
  setCitySelected,
} from "../../../actions/filters";
import LocationDropdown from "./LocationDropdown";

const PREFIX = 'Location';

const classes = {
  root: `${PREFIX}-root`,

  // migrating styles to emotion: App.theme styles no longer imported from file using useStyles
  banner: `${PREFIX}-banner`,
  bannerTextBox: `${PREFIX}-bannerTextBox`,
  bannerTextBoxHeader: `${PREFIX}-bannerTextBoxHeader`,
  bannerTextBoxSubHeader: `${PREFIX}-bannerTextBoxSubHeader`,
  bannerTextBoxButton: `${PREFIX}-bannerTextBoxButton`,
  infoIcon: `${PREFIX}-infoIcon`,

  title: `${PREFIX}-title`,
  remove: `${PREFIX}-remove`,
  select: `${PREFIX}-select`,
  header: `${PREFIX}-header`,
};

const Root = styled('div')((
  {
    theme
  }
) => ({

  [`& .${classes.banner}`]: {
    position: "relative",
    maxWidth: '100vw'
    },

  [`& .${classes.bannerTextBox}`]: {
    position: "absolute",
    left: "12vw",
    top: "5rem",
    display: "flex",
    flexDirection: "column",
    maxWidth: "20rem",
    background: "#fff",
    borderRadius: "5px",
    padding: "2rem",
    lineHeight: "1.5",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "16rem",
      top: "0",
      right: "0",
      bottom: "0",
      left: "0",
      margin: "2rem auto",
    },
  },

  [`& .${classes.bannerTextBoxHeader}`]: {
    color: "#0078a9",
    fontWeight: "bold",
    fontSize: "1.3rem",
    },

  [`& .${classes.bannerTextBoxSubHeader}`]: {
	margin: "1rem 0 0 0",
    },

  [`& .${classes.bannerTextBoxButton}`]: {
    background: "#0078a9",
    color: "#fff",
    textTransform: "none",
    display: "flex",
    alignSelf: "end",
    marginTop: "1.5em",
    "&:hover": {
      background: "#006c98",
    },
  },

  [`& .${classes.infoIcon}`]: {
    color: '#0078a9',
    fontSize: '0.9rem',
    margin: '0 0.25rem'
    },

  [`& .${classes.title}`]: {
    color: "#4c4c4c",
    fontSize: "0.9rem",
    margin: "0 0 0.5em 0",
    textTransform: "uppercase",
  },

  [`& .${classes.remove}`]: {
    background: "#999999",
    borderRadius: "50%",
    padding: "0em",
    color: "#fff",
    position: "absolute",
    right: "0.5em",
    top: "0.5em",
    "&:hover": {
      background: "#909090",
      boxShadow:
        "0 10px 15px -3px rgba(0, 0, 0, 0.2), 0 4px 6px -2px rgba(0, 0, 0, 0.1)",
    },
    "& .MuiSvgIcon-root": {
      fontSize: "1.1rem",
    },
  },

  [`& .${classes.select}`]: {
    width: "100%",
    margin: "0.5rem 0",
    "&:first-child": {
      margin: "0rem 0 0.5rem 0",
    },
    "&:last-child": {
      margin: "0.5rem 0 1rem 0",
    },
  },

  [`& .${classes.header}`]: {
    display: "flex",
    textAlign: "center",
    width: "max-content",
    "&:hover": {
      cursor: "pointer",
    },
  },

}));

const Location = ({
  getStates,
  getCounties,
  getCities,
  setStateSelected,
  setCountySelected,
  setCitySelected,
  stateSelected,
  countySelected,
  citySelected,
  states,
  counties,
  cities,
}) => {
	
  const stateRef = useRef();
  const countyRef = useRef();
  const cityRef = useRef();

  const TooltipText = () => {
    return (
      <Fragment>
        <h3>Location</h3>
        <p>Shows all events within the selected location.</p>
      </Fragment>
    );
  };

  const remove = (e) => {
    setStateSelected(undefined);
    setCountySelected(undefined);
    setCitySelected(undefined);
    getStates();
    getCounties();
    getCities();
    stateRef.current.querySelector("input").value = "";
    countyRef.current.querySelector("input").value = "";
    cityRef.current.querySelector("input").value = "";
  };

  const onChangeStates = (e, v) => {
    if (v) {
      const selected = states.filter((state) => state.name === v)[0];
      setStateSelected(selected);
      getCounties({ state: selected.id });
      getCities({ state: selected.id });
    } else {
      setStateSelected(undefined);
      getCounties();
      getCities();
    }
  };

  const onChangeCounties = (e, v) => {
    if (v) {
      const selected = counties.filter((county) => county.name === v)[0];
      setCountySelected(selected);
      getStates({ county: selected.id });
      getCities({ county: selected.id });
    } else {
      setCountySelected(undefined);
      getStates();
      getCities();
    }
  };

  const onChangeCities = (e, v) => {
    if (v) {
      const selected = cities.filter((city) => city.name === v)[0];
      setCitySelected(selected);
      getStates({ city: selected.id });
      getCounties({ city: selected.id });
    } else {
      setCitySelected(undefined);
      getStates();
      getCounties();
    }
  };

  useEffect(() => {
    getStates();
    getCounties();
    getCities();
  }, []);

  return (
    <Root className={classes.root}
	sx={{
		 border: "1px solid #e9e9e9",
    	 borderRadius: "5px",
    	 padding: "1em",
    	 position: "relative",
    	 margin: "1em 0",
    	 background: "#fff",
		 "& .MuiFormLabel-root": {
			color: "rgba(0,0,0,0.54)",
		},
	}}>
      <HtmlTooltip
        disableFocusListener
        disableTouchListener
        title={<TooltipText />}
        placement="top"
      >
        <div className={classes.header}>
          <div className={classes.title}>Location</div>
          <InfoIcon className={classes.infoIcon} />
        </div>
      </HtmlTooltip>
      <IconButton
        className={classes.remove}
        onClick={remove}
        alt="clear"
        aria-label="clear scale filter"
      >
        <RemoveIcon />
      </IconButton>
      <LocationDropdown
        componentRef={stateRef}
        id="state"
        label="State"
		variant="standard"
        options={states.map((location) => location.name)}
        onChange={onChangeStates}
        value={stateSelected.name ? stateSelected.name : ""}
      />
      <LocationDropdown
        componentRef={countyRef}
        id="county"
        label="County"
		variant="standard"
        options={counties.map((location) => location.name)}
        onChange={onChangeCounties}
        value={countySelected.name ? countySelected.name : ""}
      />
      <LocationDropdown
        componentRef={cityRef}
        id="city"
        label="City/Town"
		variant="standard"
        options={cities.map((location) => location.name)}
        onChange={onChangeCities}
        value={citySelected.name ? citySelected.name : ""}
      />
    </Root>
  );
};

Location.propTypes = {
  states: PropTypes.array.isRequired,
  counties: PropTypes.array.isRequired,
  cities: PropTypes.array.isRequired,
  getStates: PropTypes.func.isRequired,
  getCounties: PropTypes.func.isRequired,
  getCities: PropTypes.func.isRequired,
  setStateSelected: PropTypes.func.isRequired,
  setCountySelected: PropTypes.func.isRequired,
  setCitySelected: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  states: state.filters.locations.states,
  counties: state.filters.locations.counties,
  cities: state.filters.locations.cities,
  stateSelected: state.filters.stateSelected,
  countySelected: state.filters.countySelected,
  citySelected: state.filters.citySelected,
});

export default connect(mapStateToProps, {
  getStates,
  getCounties,
  getCities,
  setStateSelected,
  setCountySelected,
  setCitySelected,
})(Location);
